/**
 * components/alert.tsx — the design's `.t-alert` (+ tones).
 * Leading icon defaults per tone; children carry the message (rich nodes ok).
 */
import React from 'react';
import { View, StyleProp, ViewStyle } from 'react-native';
import { LightColors as C, Radii as R, FontSize as FS } from '../theme/theme';
import { toneColors } from '../theme/tones';
import Icon, { IconName } from './icon';

export type AlertTone = 'info' | 'success' | 'warn' | 'danger';

const DEFAULT_ICON: Record<AlertTone, IconName> = {
  info: 'info',
  success: 'check',
  warn: 'warn-tri',
  danger: 'alert-circle',
};

export interface AlertProps {
  tone?: AlertTone;
  icon?: IconName | null;
  iconSize?: number;
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}

const Alert: React.FC<AlertProps> = ({ tone = 'info', icon, iconSize = 15, children, style }) => {
  const tc = toneColors(tone);
  const iconName = icon === null ? null : icon ?? DEFAULT_ICON[tone];

  return (
    <View
      style={[
        {
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 10,
          paddingVertical: 12,
          paddingHorizontal: 14,
          borderWidth: 1,
          borderColor: tc.line,
          backgroundColor: tc.soft,
          borderRadius: R.r3,
        },
        style,
      ]}
    >
      {iconName && (
        <View style={{ marginTop: 1 }}>
          <Icon name={iconName} size={iconSize} color={tc.fg} />
        </View>
      )}
      <View style={{ flex: 1, minWidth: 0 }}>{children}</View>
    </View>
  );
};

export default Alert;

/** Convenience text color for alert message bodies. */
export { FS as AlertFontSize };
